import * as z from 'zod';
import { auditChainStatusSchema } from './audit.js';
import { batchLabelSchema, batchStatusSchema } from './batch.js';
import { isoDateSchema, isoDateTimeSchema, machineKeySchema, strict, uuidSchema, } from './common.js';
import { credentialOriginSchema } from './credential.js';
import { mealReasonCodeSchema, MEALS } from './dining.js';
import { cappedListSchema } from './pagination.js';
import { personStatusSchema } from './person.js';
import { presenceReasonCodeSchema } from './presence.js';
/**
 * Section 1.12's contract, as nine panels.
 *
 * **One endpoint per PANEL, not per metric.** Section 1.12 is a table of metrics grouped into
 * Live, Traffic, Registry, Integrity, Operators, Security, API and System, and a dashboard renders
 * a panel at a time: an endpoint per metric would be twenty-one requests to draw one screen, each
 * with its own cache, its own timezone stamp and its own chance to be computed against a different
 * instant from the panel beside it. A panel is therefore one query set, one instant and one cache
 * entry.
 *
 * **Every response states the timezone it was computed in, and it is not decoration.** Every day
 * and hour boundary in this file is a boundary in `REPORTING_TIMEZONE`, computed by the database
 * with `at time zone`, so a figure is only meaningful beside the zone that cut it — the same
 * figure computed in UTC differs by two hours' worth of movements for a facility in Madrid, and
 * across a DST change it differs by a whole bucket. `computedAt` and `cacheTtlSeconds` travel for
 * the same reason: a panel that may be up to thirty seconds stale has to be able to say so rather
 * than have an operator infer it.
 *
 * **`SQL` and `PROJ` are both here and the difference is invisible to a client, deliberately.**
 * The SQL half aggregates cleartext metadata columns and decrypts nothing. The PROJ half — data
 * completeness, nationality, age bands and the passport-expiry watch — is counted out of the
 * in-memory projection, because those four are the only demographic dimensions in Section 1.12 and
 * persisting them would put a cleartext demographic breakdown of the facility's population on disk,
 * in the dump and in every retained archive, where it would also survive a cryptographic erasure.
 * A client should not have to know which half a number came from; what it must be able to see is
 * when the number was computed and against which day.
 *
 * **Nothing in this file names a person.** Every per-person figure is a uuid and a count. The
 * evacuation roll-call is the one place a name and a photograph belong, and it is its own route
 * under `/api/v1/presence/roll-call` — there is deliberately no second endpoint answering "who is
 * inside" from these panels.
 */
/**
 * The stamp every panel carries.
 *
 * Spread into each panel rather than nested under a `meta` member, because a client reading
 * `panel.timezone` is one indirection closer to the figure it qualifies, and a nested envelope is
 * the shape that gets dropped by a mapper on the way to a chart.
 */
const STAMP = {
    /** The IANA zone every boundary in this response was cut in. */
    timezone: z.string().min(1).max(64),
    computedAt: isoDateTimeSchema,
    /** How long this panel may be served from cache. Zero means it is computed per request. */
    cacheTtlSeconds: z.int().nonnegative(),
};
/**
 * The widest range any panel will answer, in days, and the much narrower one for hour buckets.
 *
 * **Every other series in the analytics module is capped** — twenty-five top visitors, two hundred
 * operator actions, fifty routes — and the date range was the one input that was not, which made it
 * the one place a single admin request could ask for an unbounded amount of work. `granularity=hour`
 * over ten years is 87,600 buckets serialised into one response, on top of a full scan of
 * `presence_events` with a per-row lateral. Five years of daily buckets is 1,827 points, which is
 * more than any chart renders and still a bounded answer; a month of hourly ones is 744.
 *
 * These are limits on the QUESTION rather than on the data: a wider report is a range asked for in
 * pieces, and every panel states the range it answered.
 */
export const ANALYTICS_MAX_RANGE_DAYS = 1830;
export const ANALYTICS_MAX_HOUR_RANGE_DAYS = 31;
/**
 * The range a panel answers when the caller names neither end.
 *
 * Here rather than in `analytics/service.ts`, where it used to live, because the test that asserts
 * every row cap below clears what the DEFAULT question can produce has to read one number rather
 * than restate 30. A change to this default that started truncating the common case would
 * otherwise be invisible.
 */
export const DEFAULT_ANALYTICS_RANGE_DAYS = 30;
/**
 * How many rows each capped list of Section 1.12 returns, and therefore its `maxItems`.
 *
 * **Here rather than in the service, because the schema and the SQL must read the SAME number.**
 * The browser parses every response against these schemas, so a `LIMIT` raised without the ceiling
 * is a panel that goes blank with nothing failing on the server. One frozen record rather than
 * eleven constants, so a test iterates them instead of naming ten and quietly omitting the one
 * added last.
 *
 * **Every cap clears what the default range can produce**, so the common case is never truncated:
 * the widest of them, `scanAttempts`, is a day-by-outcome grid whose default-range maximum is 31
 * days times the seven `scanAttemptOutcome` values, or 217.
 *
 * **What makes each cap SAFE is the order it is applied after**, which is stated per query in
 * `analytics/sql.ts`: a cut that keeps the largest counts can only discard the smallest, which is
 * why the two AD-37 lists — `operatorRecordViews` and `apiKeyRecords` — can be capped at all. A
 * subject over its threshold disappears from those only if more than a hundred subjects are over it
 * at once, at which point the alert count beside them is the thing being read.
 *
 * **Annotated pure because only the SERVER reads this table.** The browser gets these numbers
 * baked into each schema's `maxItems` and never names it, but `Object.freeze` is a call and a
 * bundler keeps a call it cannot prove is side-effect free — so without the annotation this
 * ships to every operator as dead weight, in a bundle held to a ceiling that only moves down.
 */
export const ANALYTICS_CAP_PEAKS = 400;
export const ANALYTICS_CAP_TOP_VISITORS = 25;
export const ANALYTICS_CAP_COMPLETENESS = 50;
export const ANALYTICS_CAP_OVERRIDES_BY_OPERATOR = 100;
export const ANALYTICS_CAP_SCAN_ATTEMPTS = 350;
export const ANALYTICS_CAP_OPERATOR_ACTIONS = 200;
export const ANALYTICS_CAP_OPERATOR_RECORD_VIEWS = 100;
export const ANALYTICS_CAP_API_KEY_RECORDS = 100;
export const ANALYTICS_CAP_API_PER_KEY = 100;
export const ANALYTICS_CAP_API_PER_ROUTE = 50;
export const ANALYTICS_CAP_KEYS_EXPIRING_SOON = 100;
/**
 * The same eleven numbers as one table, for the server that applies them and the test that iterates
 * them.
 *
 * **Eleven literals plus a view rather than a table the schemas read, and the reason is bytes.** A
 * schema written as `ANALYTICS_LIST_CAPS.peaks` makes the browser carry this object — eleven long
 * keys that gzip badly — to read numbers it already has baked into each `maxItems`. Written against
 * the literals above, the minifier folds each to a number and the annotation lets the bundler drop
 * the table, which is dead weight in a bundle held to a ceiling that only moves down. There is
 * still exactly ONE number per cap; this is a view of them, not a second copy.
 */
export const ANALYTICS_LIST_CAPS = /* @__PURE__ */ Object.freeze({
    peaks: ANALYTICS_CAP_PEAKS,
    topVisitors: ANALYTICS_CAP_TOP_VISITORS,
    completeness: ANALYTICS_CAP_COMPLETENESS,
    overridesByOperator: ANALYTICS_CAP_OVERRIDES_BY_OPERATOR,
    scanAttempts: ANALYTICS_CAP_SCAN_ATTEMPTS,
    operatorActions: ANALYTICS_CAP_OPERATOR_ACTIONS,
    operatorRecordViews: ANALYTICS_CAP_OPERATOR_RECORD_VIEWS,
    apiKeyRecords: ANALYTICS_CAP_API_KEY_RECORDS,
    apiPerKey: ANALYTICS_CAP_API_PER_KEY,
    apiPerRoute: ANALYTICS_CAP_API_PER_ROUTE,
    keysExpiringSoon: ANALYTICS_CAP_KEYS_EXPIRING_SOON,
});
/**
 * The ceilings for the series that are NOT capped, derived from the range cap rather than written.
 *
 * A chart drawn from a quarter of its points is a lie, and `charts/figure.tsx` mirrors every chart
 * into a visually-hidden table whose whole job is to carry exactly what the picture shows — so a
 * chart series is bounded by the QUESTION, which the range cap already bounds, and paging it would
 * hand a screen-reader user a quarter of what a sighted one gets.
 *
 * **They must be unreachable rather than usually true**, because the browser rejects a response
 * that fails its schema: the week and month figures therefore carry a deliberate `+ 1` of slack for
 * the partial bucket at each end.
 */
export const ANALYTICS_MAX_DAY_BUCKETS = ANALYTICS_MAX_RANGE_DAYS + 1;
export const ANALYTICS_MAX_WEEK_BUCKETS = Math.ceil(ANALYTICS_MAX_DAY_BUCKETS / 7) + 1;
export const ANALYTICS_MAX_MONTH_BUCKETS = Math.ceil(ANALYTICS_MAX_DAY_BUCKETS / 28) + 1;
/** Seven weekdays by twenty-four hours, by construction. */
export const ANALYTICS_MAX_HEATMAP_CELLS = 7 * 24;
/** The eight `DWELL_BUCKET_BOUNDS` plus the open-ended tail. */
export const DWELL_BUCKET_COUNT = 9;
/**
 * How many countries the nationality distribution names before the rest fold into `other`.
 *
 * **Here rather than in the projection that slices it, and the projection IMPORTS it.** This number
 * is also the list's `maxItems`, and the browser rejects a response that exceeds one — so a second
 * copy on the server would be a number somebody could raise on one side, with the Registry panel
 * going blank in every browser and nothing failing here. One constant, read by both.
 */
export const TOP_COUNTRY_COUNT = 15;
/**
 * The seven bands `analytics-projection.ts` declares, plus `unknown`.
 *
 * A hand count of a list this package cannot see, which is the same hazard one layer along, so
 * `analytics/__tests__/ceilings.test.ts` pins it against `AGE_BANDS.length` in the package where
 * both are visible. Same for {@link DWELL_BUCKET_COUNT} and `DWELL_BUCKET_BOUNDS`.
 */
export const AGE_BAND_COUNT = 8;
/** Days between two civil dates, both parsed as UTC midnight, which is what the caller sent. */
function rangeDays(from, to) {
    return (Date.parse(to) - Date.parse(from)) / 86_400_000;
}
/**
 * Ordered, and no wider than the cap.
 *
 * **Both ends present. An absent end is not "no range", it is a range the SERVER completes**, and
 * this predicate has no clock to complete it with — so the schema below can only pass such a
 * request through, and `analytics/service.ts` re-applies the same rule once it has filled the
 * missing end in. Exported for exactly that caller, so the cap is one predicate rather than two
 * that can disagree: a second spelling of it would be the one that kept admitting `from=1900-01-01`.
 */
export function withinAnalyticsRange(range, maxDays) {
    return range.from <= range.to && rangeDays(range.from, range.to) <= maxDays;
}
/** The schema's half: checkable only when the caller spelled both ends out. See above. */
function withinRange(range, maxDays) {
    if (range.from === undefined || range.to === undefined) {
        return true;
    }
    return withinAnalyticsRange({ from: range.from, to: range.to }, maxDays);
}
/**
 * Reports a range this product will not answer, as the reason it will not.
 *
 * **`withinAnalyticsRange` stays the one predicate that decides acceptance**, exactly as its own
 * note requires: a second spelling of the cap is the one that would keep admitting `from=1900-01-01`.
 * The ordering test below it is a DISCRIMINATOR and nothing more — it runs only once the range has
 * already been refused, and only to choose which of the two sentences to say. It is not a second
 * acceptance rule and must not be turned into one.
 *
 * A `superRefine` rather than two `refine`s because the cap is a value: `trafficQuerySchema` takes
 * its own from `granularity`, and a message reading "the period cannot be longer than {{max}} days"
 * has to carry that number. The library copies a check's `params` onto the issue verbatim, which is
 * the only channel a refinement has — a `custom` issue carries no bound of its own.
 */
function refuseRange(range, maxDays, ctx) {
    if (withinRange(range, maxDays)) {
        return;
    }
    const inverted = range.from !== undefined && range.to !== undefined && range.from > range.to;
    ctx.addIssue(inverted
        ? { code: 'custom', message: 'errors.validation.range_inverted', path: ['to'] }
        : {
            code: 'custom',
            message: 'errors.validation.range_too_wide',
            params: { max: maxDays },
            path: ['to'],
        });
}
/** A civil-date range in the reporting timezone. Both ends inclusive; both default on the server. */
export const analyticsRangeQuerySchema = strict({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
}).superRefine((range, ctx) => {
    refuseRange(range, ANALYTICS_MAX_RANGE_DAYS, ctx);
});
/** How wide a traffic bucket is. An hour for a day's detail, a day for a range. */
export const analyticsGranularitySchema = z.enum(['hour', 'day']);
/** The traffic panel's own query: a range, and how finely to cut it. */
export const trafficQuerySchema = strict({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    granularity: analyticsGranularitySchema.default('day'),
    /** "People not seen in N days", which Section 1.12 leaves to the operator to choose. */
    notSeenDays: z.coerce.number().int().min(1).max(3650).default(30),
}).superRefine((range, ctx) => {
    refuseRange(range, range.granularity === 'hour' ? ANALYTICS_MAX_HOUR_RANGE_DAYS : ANALYTICS_MAX_RANGE_DAYS, ctx);
});
// -------------------------------------------------------------------------------------------------
// Live
// -------------------------------------------------------------------------------------------------
/**
 * The Live panel: how many people are out, how that compares with yesterday, and who is overdue.
 *
 * **The panel leads with `outside`, and the day-over-day comparison is the one that belongs to it.**
 * In a residence `inside` is very nearly the register's size and moves by a handful; what an
 * administrator is reading this panel for is how many people are out right now and whether that is
 * unusual for the hour. Keeping the comparison on `inside` would have left the leading figure with
 * no yesterday and the yesterday figure with no lead.
 *
 * **The comparison is against the same HOUR yesterday**, which is what an operator means: a
 * building is not comparable with itself twelve hours ago, and a day-over-day figure taken at a
 * different time of day is noise. It is reconstructed from `presence_events` rather than stored,
 * because occupancy is a fold over the movement log and a stored hourly snapshot would be a second
 * source of truth that can disagree with the first.
 *
 * **`outsideTooLong` is a COUNT and not a list.** The list of names is the outside monitor, beside
 * the roll-call, which is where this product decrypts a name for a screen.
 */
export const liveAnalyticsSchema = strict({
    ...STAMP,
    /** How many people are out right now. The figure this panel exists for. */
    outside: z.int().nonnegative(),
    outsideSameHourYesterday: z.int().nonnegative(),
    /** `outside - outsideSameHourYesterday`. Signed, and the sign is the whole point. */
    delta: z.int(),
    /** How many are inside. Very nearly the register, and reported because a reader expects it. */
    inside: z.int().nonnegative(),
    outsideTooLong: strict({
        /** `presence.outside_too_long_minutes` as it stands right now. */
        minutes: z.int().positive(),
        people: z.int().nonnegative(),
    }),
});
// -------------------------------------------------------------------------------------------------
// Traffic
// -------------------------------------------------------------------------------------------------
/** One bucket of the traffic series. `bucket` is a civil instant in the reporting timezone. */
export const trafficBucketSchema = strict({
    /** `YYYY-MM-DD` for a day bucket, `YYYY-MM-DDTHH` for an hour one. Local, never UTC. */
    bucket: z.string().min(10).max(13),
    entries: z.int().nonnegative(),
    exits: z.int().nonnegative(),
});
/** One cell of the weekday-by-hour heatmap. Monday is 1 and Sunday is 7, as ISO 8601 numbers them. */
export const trafficHeatmapCellSchema = strict({
    isoWeekday: z.int().min(1).max(7),
    hour: z.int().min(0).max(23),
    entries: z.int().nonnegative(),
});
/** The busiest moment of one day, and how many people were out at once. */
export const peakOutsideSchema = strict({
    day: isoDateSchema,
    peak: z.int().nonnegative(),
    /** The instant the peak was reached. UTC on the wire; the day it belongs to is local. */
    at: isoDateTimeSchema,
});
/**
 * One bar of the dwell-time histogram.
 *
 * `upToMinutes` is the bucket's EXCLUSIVE upper bound, so an excursion of exactly 60 minutes belongs
 * to the bar above 60 rather than to it. That is the database's own `width_bucket` convention, and
 * restating it inclusively would mean subtracting a minute somewhere — a fudge that shows up as an
 * off-by-one at exactly one value per bar. `null` is the open-ended last bucket.
 *
 * The bounds are stated rather than derived so a chart's axis is stable across releases and across
 * a range with nothing in its tail.
 */
export const dwellBucketSchema = strict({
    upToMinutes: z.int().positive().nullable(),
    count: z.int().nonnegative(),
});
/**
 * How long an excursion lasts, over PAIRED events.
 *
 * An excursion is an exit followed by that person's next entry, so somebody still outside
 * contributes nothing and an entry with no exit before it contributes nothing either. `pairs` is
 * therefore the denominator every other figure here is over, and it is reported so that a mean over
 * four excursions is not read as a mean over four hundred.
 *
 * It measures the OUTSIDE side, which is the side this facility has a policy about. Measuring how
 * long a resident has been in is measuring how long they have lived here.
 */
export const dwellStatsSchema = strict({
    pairs: z.int().nonnegative(),
    meanMinutes: z.number().nonnegative().nullable(),
    medianMinutes: z.number().nonnegative().nullable(),
    p90Minutes: z.number().nonnegative().nullable(),
    histogram: z.array(dwellBucketSchema).max(DWELL_BUCKET_COUNT),
});
/** One person's visit count. A uuid and a number; no name reaches an analytics panel. */
export const visitCountSchema = strict({
    personId: uuidSchema,
    visits: z.int().nonnegative(),
});
/**
 * Section 1.12's "visits per person; people never scanned; people not seen in N days".
 *
 * `topVisitors` is bounded rather than complete: the per-person series over five thousand people is
 * a table nobody reads and a response nobody should have to page through to draw a chart. The two
 * counts beside it are the figures an operator acts on — a record that has never been scanned is
 * usually a registration that was never finished, and one not seen in N days is the retention
 * warning window's own population.
 */
export const visitAnalyticsSchema = strict({
    topVisitors: cappedListSchema(visitCountSchema, ANALYTICS_CAP_TOP_VISITORS),
    neverScanned: z.int().nonnegative(),
    notSeenInDays: strict({ days: z.int().positive(), people: z.int().nonnegative() }),
});
export const trafficAnalyticsSchema = strict({
    ...STAMP,
    from: isoDateSchema,
    to: isoDateSchema,
    granularity: analyticsGranularitySchema,
    buckets: z.array(trafficBucketSchema).max(ANALYTICS_MAX_DAY_BUCKETS),
    heatmap: z.array(trafficHeatmapCellSchema).max(ANALYTICS_MAX_HEATMAP_CELLS),
    peaks: cappedListSchema(peakOutsideSchema, ANALYTICS_CAP_PEAKS),
    dwell: dwellStatsSchema,
    visits: visitAnalyticsSchema,
});
// -------------------------------------------------------------------------------------------------
// Registry
// -------------------------------------------------------------------------------------------------
/** One bucket of the registrations series. */
export const registrationBucketSchema = strict({
    bucket: z.string().min(7).max(10),
    count: z.int().nonnegative(),
});
/**
 * The share of active people with no value for one warn-level field (PROJ).
 *
 * **`population` rather than `total`, and the rename is not cosmetic.** This list is capped, so it
 * travels inside an envelope whose own `total` counts FIELDS; a member here called `total` counting
 * PEOPLE would put two different quantities one word apart on one screen. It is the denominator
 * `share` is over, and `population` is what it is.
 */
export const completenessFigureSchema = strict({
    fieldKey: z.string().min(1).max(64),
    missing: z.int().nonnegative(),
    population: z.int().nonnegative(),
    /** `missing / population`, or `0` when there is nobody to be incomplete. */
    share: z.number().min(0).max(1),
});
/**
 * The passport-expiry watch, as DISJOINT buckets (PROJ).
 *
 * Section 1.12 asks for "expired, and expiring within 30, 60 and 90 days", which reads as three
 * nested windows. They are disjoint here, because a nested set cannot be recovered from an
 * overlapping one while the cumulative figures are two additions away — and because a chart of four
 * overlapping numbers adds up to more than the population.
 */
export const passportExpiryFigureSchema = strict({
    expired: z.int().nonnegative(),
    within30: z.int().nonnegative(),
    within60: z.int().nonnegative(),
    within90: z.int().nonnegative(),
    later: z.int().nonnegative(),
    none: z.int().nonnegative(),
});
/** The nationality distribution: the leaders, everybody else, and the blanks (PROJ). */
export const nationalityFigureSchema = strict({
    top: z
        .array(strict({ country: z.string().length(2), count: z.int().nonnegative() }))
        .max(TOP_COUNTRY_COUNT),
    other: z.int().nonnegative(),
    unknown: z.int().nonnegative(),
    distinctCountries: z.int().nonnegative(),
});
/** One age band and its population (PROJ). A birth COHORT, since only the birth year is projected. */
export const ageBandFigureSchema = strict({
    band: z.string().min(1).max(16),
    count: z.int().nonnegative(),
});
/** What is left in one box of pre-printed bands. */
export const passStockBatchSchema = strict({
    batchId: uuidSchema,
    label: batchLabelSchema,
    status: batchStatusSchema,
    available: z.int().nonnegative(),
    claimed: z.int().nonnegative(),
    voided: z.int().nonnegative(),
    importedAt: isoDateTimeSchema,
});
/**
 * Section 1.12's pass-stock panel.
 *
 * `available` is the figure `passes.low_blank_threshold` is compared against, and it counts only
 * batches that are still ACTIVE: a blank of a voided batch is not stock, and counting it would keep
 * the low-stock alert quiet exactly when a whole box has just been withdrawn.
 */
export const passStockSchema = strict({
    available: z.int().nonnegative(),
    claimed: z.int().nonnegative(),
    voided: z.int().nonnegative(),
    threshold: z.int().nonnegative(),
    low: z.boolean(),
    /** The oldest batch with anything left in it, which is the box to open next. */
    oldestActiveBatch: passStockBatchSchema.nullable(),
});
export const registryAnalyticsSchema = strict({
    ...STAMP,
    from: isoDateSchema,
    to: isoDateSchema,
    totalsByStatus: z
        .array(strict({ status: personStatusSchema, count: z.int().nonnegative() }))
        .max(personStatusSchema.options.length),
    /** Erased records, which have no `status` left to be counted under. */
    shredded: z.int().nonnegative(),
    registrations: strict({
        perDay: z.array(registrationBucketSchema).max(ANALYTICS_MAX_DAY_BUCKETS),
        perWeek: z.array(registrationBucketSchema).max(ANALYTICS_MAX_WEEK_BUCKETS),
        perMonth: z.array(registrationBucketSchema).max(ANALYTICS_MAX_MONTH_BUCKETS),
    }),
    /** The civil day the four PROJ figures below were computed against. */
    projectionDay: isoDateSchema,
    activeInProjection: z.int().nonnegative(),
    completeness: cappedListSchema(completenessFigureSchema, ANALYTICS_CAP_COMPLETENESS),
    passportExpiry: passportExpiryFigureSchema,
    nationality: nationalityFigureSchema,
    ageBands: z.array(ageBandFigureSchema).max(AGE_BAND_COUNT),
    /**
     * The band inventory, ABSENT rather than empty for a caller without `credential_batch:read`.
     *
     * The pre-printed bands screen is the lead administrator's, and this member is a row of the same
     * inventory: the threshold, the counts and the oldest open box by LABEL. An administrator keeps
     * the rest of this panel — the demographics and the completeness figures are what they open it
     * for — and loses the one member that restates a screen they may no longer reach. Optional, not
     * zeroed, for the reason `http/redact-pii.ts` gives: an empty figure is a claim that there are no
     * bands left, which is a different and false statement.
     */
    passStock: passStockSchema.optional(),
});
// -------------------------------------------------------------------------------------------------
// Integrity
// -------------------------------------------------------------------------------------------------
/**
 * The override figures, counted over DISTINCT idempotency keys.
 *
 * A `rejected` outcome does not consume its key (AD-30), so a blocked person's refusal and the
 * reasoned movement the operator files next share one key; and a retried timeout would otherwise
 * be counted twice. Counting keys rather than rows is what makes "how many overrides happened" a
 * number about the world rather than about the network.
 */
export const overrideFigureSchema = strict({
    total: z.int().nonnegative(),
    byReason: z
        .array(strict({ reasonCode: presenceReasonCodeSchema, count: z.int().nonnegative() }))
        .max(presenceReasonCodeSchema.options.length),
    byDay: z
        .array(strict({ bucket: isoDateSchema, count: z.int().nonnegative() }))
        .max(ANALYTICS_MAX_DAY_BUCKETS),
    byOperator: cappedListSchema(strict({ operatorId: uuidSchema.nullable(), count: z.int().nonnegative() }), ANALYTICS_CAP_OVERRIDES_BY_OPERATOR),
});
/** Failed scans over time, by the outcome that refused them. */
export const scanAttemptFigureSchema = strict({
    bucket: isoDateSchema,
    outcome: machineKeySchema,
    count: z.int().nonnegative(),
});
export const integrityAnalyticsSchema = strict({
    ...STAMP,
    from: isoDateSchema,
    to: isoDateSchema,
    overrides: overrideFigureSchema,
    /**
     * Section 1.12's scanner-health signal.
     *
     * Its only source is the `presence.duplicate_suppressed` audit row: a replay writes no
     * `presence_events` row by construction and `scan_attempts` has no member for it.
     */
    duplicateSuppressions: z.int().nonnegative(),
    scanAttempts: cappedListSchema(scanAttemptFigureSchema, ANALYTICS_CAP_SCAN_ATTEMPTS),
    chain: auditChainStatusSchema,
    /** So an emergency mint taken in pre-printed mode is visible rather than only audited (AD-59). */
    credentialsByOrigin: z
        .array(strict({ origin: credentialOriginSchema, count: z.int().nonnegative() }))
        .max(credentialOriginSchema.options.length),
});
// -------------------------------------------------------------------------------------------------
// Operators
// -------------------------------------------------------------------------------------------------
/** One operator's count of one action. `operatorId` is null for a system or API-key action. */
export const operatorActionFigureSchema = strict({
    operatorId: uuidSchema.nullable(),
    action: z.string().min(3).max(64),
    count: z.int().nonnegative(),
});
/**
 * Section 1.12's manual-versus-scan ratio.
 *
 * Read off `presence_events.method`, which is cleartext and closed. `manual_search` is AD-53's
 * marked path and is counted as manual; `system`, `reconciliation` and `api` are neither, so they
 * are their own bucket rather than being folded into one side and quietly distorting it.
 */
export const manualVersusScanSchema = strict({
    scan: z.int().nonnegative(),
    manual: z.int().nonnegative(),
    automatic: z.int().nonnegative(),
    /** `manual / (manual + scan)`, or `null` when neither happened. */
    manualShare: z.number().min(0).max(1).nullable(),
});
export const operatorAnalyticsSchema = strict({
    ...STAMP,
    from: isoDateSchema,
    to: isoDateSchema,
    actions: cappedListSchema(operatorActionFigureSchema, ANALYTICS_CAP_OPERATOR_ACTIONS),
    manualVersusScan: manualVersusScanSchema,
    logins: strict({
        successes: z.int().nonnegative(),
        failures: z.int().nonnegative(),
        lockouts: z.int().nonnegative(),
    }),
    /** Rows in the session store that have not expired. One per signed-in terminal. */
    sessionsActiveNow: z.int().nonnegative(),
});
// -------------------------------------------------------------------------------------------------
// Security
// -------------------------------------------------------------------------------------------------
/**
 * Section 1.12's Security panel: AD-37's two thresholds and what is currently measured against them.
 *
 * The windows are the ones the alert itself uses, and they are deliberately different from each
 * other: the operator figure is the reporting timezone's current DAY, because the threshold is
 * stated per operator per day and a facility's day is the one an operator would name; the API-key
 * figure is the TRAILING hour, because a clock hour resets a rate and a key returning nine hundred
 * records at 10:55 and nine hundred more at 11:05 has crossed a thousand an hour without crossing
 * it inside either clock hour.
 */
export const securityAnalyticsSchema = strict({
    ...STAMP,
    /** The reporting timezone's current civil day, which the operator figures are over. */
    day: isoDateSchema,
    /**
     * AD-37's two limits, ABSENT for a caller without `setting:read`.
     *
     * They are SETTING VALUES, and the settings screen is the lead administrator's. What an
     * administrator keeps is `overThreshold` on each row below, which is computed here — so they
     * still see WHO has crossed a line without being told where the line is.
     */
    thresholds: strict({
        recordViewsPerOperatorPerDay: z.int().positive(),
        recordsPerApiKeyPerHour: z.int().positive(),
    }).optional(),
    operatorRecordViews: cappedListSchema(strict({
        operatorId: uuidSchema,
        views: z.int().nonnegative(),
        overThreshold: z.boolean(),
    }), ANALYTICS_CAP_OPERATOR_RECORD_VIEWS),
    /** Per-key extraction counts, ABSENT for a caller without `api_key:read`: it is key inventory. */
    apiKeyRecords: cappedListSchema(strict({
        keyId: uuidSchema,
        records: z.int().nonnegative(),
        overThreshold: z.boolean(),
    }), ANALYTICS_CAP_API_KEY_RECORDS).optional(),
    /** Open `exfiltration` alerts right now, so the panel and the alert bar cannot disagree. */
    openExfiltrationAlerts: z.int().nonnegative(),
});
// -------------------------------------------------------------------------------------------------
// API
// -------------------------------------------------------------------------------------------------
export const apiKeyUsageFigureSchema = strict({
    keyId: uuidSchema,
    name: z.string().min(1).max(200),
    calls: z.int().nonnegative(),
    errors: z.int().nonnegative(),
    /**
     * `errors / calls`, and never null.
     *
     * It used to be nullable, for "the key made no call in the range" — a row that existed only
     * because the query outer-joined from `api_keys` with the range in the JOIN predicate and
     * manufactured a zero row for every key ever issued. This list is now an aggregate over the calls
     * themselves, so a row IS a key that called and `calls` is at least one. A branch that cannot be
     * taken is a branch no test can cover.
     */
    errorRate: z.number().min(0).max(1),
    recordsReturned: z.int().nonnegative(),
    /** The key's own last use, ALL-TIME rather than within the range, and genuinely nullable. */
    lastUsedAt: isoDateTimeSchema.nullable(),
});
export const apiRouteUsageFigureSchema = strict({
    route: z.string().min(1).max(200),
    method: z.string().min(3).max(7),
    calls: z.int().nonnegative(),
    errors: z.int().nonnegative(),
    /** `errors / calls`. A row is a route that WAS called, so `calls` is at least one. */
    errorRate: z.number().min(0).max(1),
});
export const apiAnalyticsSchema = strict({
    ...STAMP,
    from: isoDateSchema,
    to: isoDateSchema,
    perKey: cappedListSchema(apiKeyUsageFigureSchema, ANALYTICS_CAP_API_PER_KEY),
    perRoute: cappedListSchema(apiRouteUsageFigureSchema, ANALYTICS_CAP_API_PER_ROUTE),
    /** Within thirty days, which is a third of the ninety-day default expiry. */
    keysExpiringSoon: cappedListSchema(strict({ keyId: uuidSchema, name: z.string().min(1).max(200), expiresAt: isoDateTimeSchema }), ANALYTICS_CAP_KEYS_EXPIRING_SOON),
});
// -------------------------------------------------------------------------------------------------
// System
// -------------------------------------------------------------------------------------------------
/**
 * Section 1.12's System panel: the runtime facts an operator checks before anything else.
 *
 * **Three members are nullable and each null means "this process could not ask".** The queue lives
 * in pg-boss's own schema and the api holds a send-only client, the object-store figures are a sum
 * over `person_images` and are exact rather than a bucket listing, and a projection figure is
 * absent in a process that holds no projection. A zero would be a lie in every one of those cases,
 * and a panel that cannot distinguish "nothing" from "unknown" is a panel that reports an outage as
 * calm.
 */
export const systemAnalyticsSchema = strict({
    ...STAMP,
    database: strict({
        bytes: z.int().nonnegative(),
        tables: z.int().nonnegative(),
    }),
    objectStore: strict({
        /** Stored objects, counting the main image and its thumbnail separately, as the bucket does. */
        objects: z.int().nonnegative(),
        /** The CIPHERTEXT bytes, which is what `person_images.bytes` records and what the volume holds. */
        bytes: z.int().nonnegative(),
    }),
    backups: strict({
        lastAt: isoDateTimeSchema.nullable(),
        lastStatus: z.enum(['running', 'ok', 'failed']).nullable(),
        lastBytes: z.int().nonnegative().nullable(),
        retained: z.int().nonnegative(),
    }),
    queue: strict({
        /** `false` when this process could not read pg-boss's schema at all. */
        available: z.boolean(),
        pending: z.int().nonnegative().nullable(),
        active: z.int().nonnegative().nullable(),
        failed: z.int().nonnegative().nullable(),
    }),
    projection: strict({
        ready: z.boolean(),
        entries: z.int().nonnegative().nullable(),
        /** The durable feed position the indexer has recorded, as a string: the column is a bigint. */
        watermark: z.string().regex(/^\d+$/u).nullable(),
        /** Feed entries above the watermark. A COUNT, never `max(seq) - watermark`; see AD-21. */
        feedBacklog: z.int().nonnegative().nullable(),
        lastReconciledAt: isoDateTimeSchema.nullable(),
    }),
    uptimeSeconds: z.int().nonnegative(),
});
// -------------------------------------------------------------------------------------------------
// Dining
// -------------------------------------------------------------------------------------------------
/**
 * How many people took how many of the three sittings, as PERSON-DAYS.
 *
 * **Four rows, always, including the zeroes**, so a chart's axis does not change shape with the
 * data. The unit is a person-day rather than a person because the panel answers a RANGE: over a
 * week, one resident who ate nothing on Tuesday contributes one row to `meals: 0` and six elsewhere.
 * The single-day answer, which is the one an administrator reads most, is the roster's own totals,
 * computed from the same rows the list is made of.
 *
 * Counting a person-day with no meals needs a denominator, so this is the one dining figure that is
 * not a plain aggregate over `meal_events`: it walks the civil days of the range against the people
 * who existed by each of them. `analytics/sql.ts` states the two limits that inherits from
 * `outsideAt`, whose shape it copies.
 */
export const MEAL_DISTRIBUTION_BUCKETS = MEALS.length + 1;
export const mealDistributionFigureSchema = strict({
    meals: z.int().min(0).max(MEALS.length),
    personDays: z.int().nonnegative(),
});
/** One civil day's three counts. Grouped on the stored `service_date`, never on a recomputed day. */
export const diningDayFigureSchema = strict({
    bucket: isoDateSchema,
    breakfast: z.int().nonnegative(),
    lunch: z.int().nonnegative(),
    dinner: z.int().nonnegative(),
});
/** One clock hour of the day and how many servings happened in it. Twenty-four rows, always. */
export const diningHourFigureSchema = strict({
    hour: z.int().min(0).max(23),
    count: z.int().nonnegative(),
});
/**
 * The Dining panel.
 *
 * **Every list here is either a fixed size or bounded by the range cap, so none of them pages.**
 * That is the rule R386 states: a chart series is bounded by the QUESTION, and a `sr-only` table
 * under a chart carries exactly what the picture shows, so truncating it would hand a
 * screen-reader user a fraction of what a sighted one gets.
 *
 * **It names nobody, which is the same rule every other panel here follows.** "Who has had nothing
 * today" is `GET /api/v1/dining/roster`, which decrypts nothing either but is separately authorized
 * under `dining:read` because it returns names. There is deliberately no second answer to it here.
 *
 * There is no `coverage` member: it would be the sum of `distribution`, which is a second answer to
 * one question and therefore the one that drifts.
 */
export const diningAnalyticsSchema = strict({
    ...STAMP,
    from: isoDateSchema,
    to: isoDateSchema,
    /** Servings in the range, counting an authorised second helping in `servedAgain` and nowhere else. */
    totals: strict({
        breakfast: z.int().nonnegative(),
        lunch: z.int().nonnegative(),
        dinner: z.int().nonnegative(),
        servedAgain: z.int().nonnegative(),
    }),
    perDay: z.array(diningDayFigureSchema).max(ANALYTICS_MAX_DAY_BUCKETS),
    distribution: z.array(mealDistributionFigureSchema).length(MEAL_DISTRIBUTION_BUCKETS),
    byHour: z.array(diningHourFigureSchema).length(24),
    overridesByReason: z
        .array(strict({ reasonCode: mealReasonCodeSchema, count: z.int().nonnegative() }))
        .max(mealReasonCodeSchema.options.length),
});
//# sourceMappingURL=analytics.js.map